/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { produce } from 'immer';
import { updateComponentArgs, updatePageErrorsArgs } from '../../context/PageContext/index.types';

// Interface for all the commonly used PageContext properties
export interface PageContextState {
  // Core page data
  pageState: any;
  currentLocale: string;
  sessionData: any;
  headersData: any;
  resolvedUrl: string;
  requestId: string;

  // Feature flags and mode indicators
  isAuthorMode: boolean;
  isDTT: boolean;
  isBulgariDomain: boolean;

  // Page-specific data
  pageVal: any;
  query: any;
  datalayerParams: any;
  datalayerProperties: any;

  // API and processing URLs
  preHeader: any;
  interceptorResponse: any;
  NEXT_PUBLIC_PRE_PROCESSING_URL: string;
  NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL: string;
  SESSION_APP_GET_CALL_URL: string;

  // Additional context data
  featureFlagLogo: any;
  setSessionData: any;

  // Component state tracking
  componentStates: Record<string, any>;
  componentErrors: Record<string, { message: string }[]>;

  // Legacy compatibility - individual flags
  resultsRetried: boolean;
}

// Interface for store actions
export interface PageContextActions {
  // Initialize the store with context data
  initializePageContextStore: (contextData: any) => void;

  // Update individual properties
  updatePageContextProperty: (key: keyof PageContextState, value: any) => void;

  // Maintain compatibility with existing PageContext API
  updateComponentState: (args: updateComponentArgs) => void;
  updatePageErrors: (args: updatePageErrorsArgs) => void;

  // Bulk update functionality
  updatePageContextState: (updates: Partial<PageContextState>) => void;

  // Reset store to initial state
  resetPageContextStore: () => void;

  // Legacy compatibility methods
  updatePageContextVar: (key: string, value: any) => void;

  // PageStateStore compatibility methods
  pageStateStoreInitialization: (memoPageContextValue: any) => void;
}

// Combined store type
export type PageContextStore = PageContextState & PageContextActions;

// Initial state matching common PageContext usage patterns
const initialState: PageContextState = {
  pageState: {},
  currentLocale: '',
  sessionData: null,
  headersData: {},
  resolvedUrl: '',
  requestId: '',
  isAuthorMode: false,
  isDTT: false,
  isBulgariDomain: false,
  pageVal: {},
  query: {},
  datalayerParams: {},
  datalayerProperties: {},
  preHeader: null,
  interceptorResponse: null,
  NEXT_PUBLIC_PRE_PROCESSING_URL: '',
  NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL: '',
  SESSION_APP_GET_CALL_URL: '',
  featureFlagLogo: null,
  setSessionData: null,
  componentStates: {},
  componentErrors: {},
  resultsRetried: false,
};

// Store creator following existing patterns
const pageContextZustandStore: StateCreator<PageContextStore> = set => {
  return {
    ...initialState,

    // Initialize store with existing context data for migration
    initializePageContextStore: (contextData: any) => {
      set(
        produce(draft => {
          // Map all known context properties
          if (contextData.pageState !== undefined) draft.pageState = contextData.pageState;
          if (contextData.currentLocale !== undefined) draft.currentLocale = contextData.currentLocale;
          if (contextData.sessionData !== undefined) draft.sessionData = contextData.sessionData;
          if (contextData.headersData !== undefined) draft.headersData = contextData.headersData;
          if (contextData.resolvedUrl !== undefined) draft.resolvedUrl = contextData.resolvedUrl;
          if (contextData.requestId !== undefined) draft.requestId = contextData.requestId;
          if (contextData.isAuthorMode !== undefined) draft.isAuthorMode = contextData.isAuthorMode;
          if (contextData.isDTT !== undefined) draft.isDTT = contextData.isDTT;
          if (contextData.isBulgariDomain !== undefined) draft.isBulgariDomain = contextData.isBulgariDomain;
          if (contextData.pageVal !== undefined) draft.pageVal = contextData.pageVal;
          if (contextData.query !== undefined) draft.query = contextData.query;
          if (contextData.datalayerParams !== undefined) draft.datalayerParams = contextData.datalayerParams;
          if (contextData.datalayerProperties !== undefined)
            draft.datalayerProperties = contextData.datalayerProperties;
          if (contextData.preHeader !== undefined) draft.preHeader = contextData.preHeader;
          if (contextData.interceptorResponse !== undefined)
            draft.interceptorResponse = contextData.interceptorResponse;
          if (contextData.NEXT_PUBLIC_PRE_PROCESSING_URL !== undefined)
            draft.NEXT_PUBLIC_PRE_PROCESSING_URL = contextData.NEXT_PUBLIC_PRE_PROCESSING_URL;
          if (contextData.NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL !== undefined)
            draft.NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL = contextData.NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL;
          if (contextData.SESSION_APP_GET_CALL_URL !== undefined)
            draft.SESSION_APP_GET_CALL_URL = contextData.SESSION_APP_GET_CALL_URL;
          if (contextData.featureFlagLogo !== undefined) draft.featureFlagLogo = contextData.featureFlagLogo;
          if (contextData.setSessionData !== undefined) draft.setSessionData = contextData.setSessionData;
        })
      );
    },

    // Update individual properties
    updatePageContextProperty: (key: keyof PageContextState, value: any) => {
      set(
        produce(draft => {
          draft[key] = value;
        })
      );
    },

    // Maintain compatibility with existing PageContext updateComponentState API
    updateComponentState: ({ componentName, newState }: updateComponentArgs) => {
      set(
        produce(draft => {
          draft.componentStates[componentName] = {
            ...draft.componentStates[componentName],
            ...newState,
          };
        })
      );
    },

    // Maintain compatibility with existing PageContext updatePageErrors API
    updatePageErrors: ({ componentName, errors }: updatePageErrorsArgs) => {
      set(
        produce(draft => {
          draft.componentErrors[componentName] = errors;
        })
      );
    },

    // Bulk update functionality for multiple properties
    updatePageContextState: (updates: Partial<PageContextState>) => {
      set(
        produce(draft => {
          Object.keys(updates).forEach(key => {
            const typedKey = key as keyof PageContextState;
            draft[typedKey] = updates[typedKey];
          });
        })
      );
    },

    // Reset store to initial state
    resetPageContextStore: () => {
      set({ ...initialState, componentStates: {}, componentErrors: {} });
    },

    // Legacy compatibility method to match old store interface
    updatePageContextVar: (key: string, value: any) => {
      set(
        produce(draft => {
          // For direct property updates, try to match to known properties first
          if (key in draft) {
            (draft as any)[key] = value;
          } else {
            // For unknown properties, store in a general properties object if needed
            // This maintains backward compatibility for any custom properties
            (draft as any)[key] = value;
          }
        })
      );
    },

    // PageStateStore compatibility method
    pageStateStoreInitialization: (memoPageContextValue: any) => {
      set(
        produce(draft => {
          // Replace entire state with the provided context value
          Object.assign(draft, memoPageContextValue);
        })
      );
    },
  };
};

// Create the store using the existing createAppStore pattern
export const usePageContextZustandStore = createAppStore(pageContextZustandStore, {
  usePersistentStore: false,
});

// Export with legacy name for backward compatibility
export const usePageContextStore = usePageContextZustandStore;

// Export with pageStateStore name for backward compatibility
export const usePageStateStore = usePageContextZustandStore;
